import React from 'react'
import styled from 'styled-components'

// Components
import Text from './Text'
import { RowAligned } from './layout/LayoutAtoms'
import Avatar from './Avatar'
import { Icon } from './icon'
import AvatarIcon from './AvatarIcon'

// Styles
import { shadows, fastTransition } from '../styles/effects'

// Utils
import { useAppState } from '../context/AppContext'

// Types
import { IconType } from './icon/IconType'
import { ICompanyMemberPure } from '../common/models/Company'

interface AvatarProps {
  big: boolean
  member: ICompanyMemberPure
  lightBackground?: boolean
  external?: boolean
}

export interface IProps {
  member: ICompanyMemberPure
  avatars?: AvatarProps[]
  iconType?: IconType
  text?: string
  onClick?: () => void
}

const AvatarHeader: React.FC<IProps> = ({
  iconType,
  member,
  text,
  avatars,
  onClick,
}) => {
  const { theme } = useAppState()

  return (
    <div>
      <Container>
        {iconType ? (
          <AvatarIcon user={member} iconType={iconType} />
        ) : avatars ? (
          <>
            <StyledAvatar
              big={avatars[0].big}
              user={avatars[0].member}
              lightBackground={avatars[0].lightBackground}
              external={avatars[0].external}
            />
            <Icon type="ArrowRight" color={theme.colors.text72} size={12} />
            <StyledAvatar
              big={avatars[1].big}
              user={avatars[1].member}
              external={avatars[1].external}
            />
          </>
        ) : (
          <StyledAvatar user={member} big />
        )}
        <MemberButton clickable={!!onClick} onClick={onClick || undefined}>
          <Text textStyle="displayS" marginRight={8}>
            {member?.name}
          </Text>
          {onClick && (
            <Icon
              type="ChevronRight"
              size={16}
              color={theme.colors.primaryFadeLight}
            />
          )}
        </MemberButton>
      </Container>
      {text && (
        <Text textStyle="bodySmall" color="textLight" marginTop={4}>
          {text}
        </Text>
      )}
    </div>
  )
}

export default AvatarHeader

const Container = styled(RowAligned)`
  flex-wrap: wrap;
`

const StyledAvatar = styled(Avatar)<{
  big?: boolean
  lightBackground?: boolean
}>`
  width: ${({ big }) => (big ? 40 : 28)}px;
  height: ${({ big }) => (big ? 40 : 28)}px;
  border: 2px solid ${({ theme }) => theme.colors.lightest};
  margin-right: 8px;
  flex-shrink: 0;
  box-shadow: ${shadows.depthTwo};

  &:last-of-type {
    margin-right: 12px;
  }

  ${({ lightBackground, theme }) =>
    lightBackground &&
    `
      background-color: ${theme.colors.primaryLight};

      span,
      svg {
        width: 12px !important;
        height: 12px !important;
      }
    `}
`

const MemberButton = styled(RowAligned)<{ clickable: boolean }>`
  padding: 8px;
  border-radius: 8px;
  margin-left: -8px;
  transition: background-color ${fastTransition};

  ${({ clickable, theme }) =>
    clickable &&
    `
    &:hover {
    cursor: pointer;
    background-color: ${theme.colors.primaryFadeLight};
    & * {
      color: ${theme.colors.primary} !important;
    }
    svg > path {
      fill: ${theme.colors.primary};
    }
  }
  `}
`
